<script lang="ts" setup>
import { ref, watch } from 'vue';
import type { VTextField } from 'vuetify/components';
import { CustomerMgmtTypes, useEditUser } from '@ilteducation/customer-mgmt';
import Modal from '../../Modal.vue';
import useModal, { ModalTypes, modals } from '../../../composables/useModal';
import { useLocale } from '../../../composables/useLocale';
import useToast from '../../../composables/useToast';
import OrganizationsTable, { AssociationData } from './OrganizationsTable.vue';
import { computed } from 'vue';
import { useSidebarUser } from '../../../composables/useSidebarUser';
import { Role } from '../../../types/user';
import { useAppEvents } from '../../../events/appEvents';

const { t } = useLocale();

type Props = {
  name: ModalTypes;
  user: CustomerMgmtTypes.GetUserResponse;
};

const props = defineProps<Props>();
const loading = ref<boolean>(false);
const { deleteAssociation, upsertAssociations } = useEditUser();
const { getUser } = useSidebarUser();
const { emitAppEvent } = useAppEvents();
const { close, isOpen } = useModal(props.name);
const { open: openDeleteUserModal } = useModal(modals.DELETE_USER);
const { toast } = useToast();

const storedAssociations = computed(
  () =>
    new Map<string, AssociationData>(
      props.user.associations.map((association) => [
        association.schoolUnit.uid,
        {
          schoolUid: association.schoolUnit.uid,
          role: association.isAdministrator ? 'admin' : association.role,
        },
      ]),
    ),
);

const associationsState = ref<Map<string, AssociationData>>(storedAssociations.value);

watch(storedAssociations, () => {
  associationsState.value = storedAssociations.value;
});

const changes = computed(() => {
  const keys = Array.from(new Set([...storedAssociations.value.keys(), ...associationsState.value.keys()]));
  return keys.reduce<{
    upsert: AssociationData[];
    remove: AssociationData[];
  }>(
    (acc, key) => {
      const stored = storedAssociations.value.get(key);
      const state = associationsState.value.get(key);

      if (stored && !state) {
        // Removed
        acc.remove.push(stored);
      } else if (!stored && state) {
        // Added
        acc.upsert.push(state);
      } else if (stored && state && stored.role !== state.role) {
        // Updated
        acc.upsert.push(state);
      }

      return acc;
    },
    { upsert: [], remove: [] },
  );
});

const onSave = async () => {
  loading.value = true;
  let isSaved = true;
  const { upsert, remove } = changes.value;

  try {
    const upsertResult =
      upsert.length > 0
        ? await upsertAssociations(
            props.user.uid,
            upsert
              .filter((data): data is { schoolUid: string; role: Role } => data.role !== null)
              .map((data) => ({
                schoolUid: data.schoolUid,
                role: data.role,
              })),
          )
        : true;

    const removeResult = await Promise.all(remove.map((data) => deleteAssociation(props.user.uid, data.schoolUid)));

    if ([upsertResult, ...removeResult].some((success) => !success)) {
      throw new Error('Failed to save all changes');
    }
    toast(t('lcp.user_organizations_successfully_saved'), 'success');
  } catch (e) {
    console.error(e);
    isSaved = false;
    toast(t('lcp.user_organizations_save_failed'), 'error');
  }

  await getUser(props.user.uid, true);
  emitAppEvent({ type: 'association:updated', userUids: [props.user.uid] });
  loading.value = false;

  if (isSaved) {
    close();
    reset();
  }
};

// Unselecting every organization deletes the user, so it is confirmed as a deletion instead of saved.
const onDeleteUser = () => {
  openDeleteUserModal({
    organizationUids: changes.value.remove.map((association) => association.schoolUid),
    onDeleted: close,
    onFailure: () => getUser(props.user.uid, true),
  });
};

watch(props.user, () => {
  reset();
});

watch(isOpen, (newValue) => {
  if (!newValue) {
    reset();
  }
});

const willLeaveUserWithoutOrganizations = computed(
  () => storedAssociations.value.size > 0 && associationsState.value.size === 0,
);

const hasChange = computed(() => {
  const { upsert, remove } = changes.value;
  return upsert.length + remove.length > 0;
});

const allAssociationsHasSelectedRole = computed(() => {
  const { upsert } = changes.value;
  return upsert.every((association) => association.role !== undefined && association.role !== null);
});

const actionLabel = computed(() => {
  if (willLeaveUserWithoutOrganizations.value) {
    return t('lcp.delete_user');
  }

  const { upsert, remove } = changes.value;
  const totalChange = upsert.length + remove.length;
  if (totalChange > 0) {
    return `${t('lcp.save')}(${totalChange})`;
  }

  return t('lcp.save');
});

const searchRef = ref<VTextField>();
const search = ref<string>();
const searchString = ref<string>();

const searchChanged = () => {
  searchString.value = searchRef.value?.value ?? undefined;
  search.value = searchString.value;
};

const clearSearch = () => {
  searchString.value = undefined;
  search.value = searchString.value;
};

const reset = () => {
  clearSearch();
  associationsState.value = storedAssociations.value;
};
</script>
<template>
  <Modal
    :loading="loading"
    background-color="surface-container"
    :name="name"
    :title="t('lcp.manage_user_organizations_for', { username: user.name })"
    :action-label="actionLabel"
    :action="willLeaveUserWithoutOrganizations ? onDeleteUser : onSave"
    :action-color="willLeaveUserWithoutOrganizations ? 'error' : 'primary'"
    :action-disabled="!hasChange || !allAssociationsHasSelectedRole"
    :show-cancel-action="true"
    :clearable="true"
    width="970"
    :persistent-clear="true"
    :cancel-action-label="t('lcp.cancel')"
    secondary-action-variant="tonal"
    scrollable
  >
    <v-row>
      <v-col :cols="true">
        <v-text-field
          ref="searchRef"
          v-model="search"
          append-inner-icon="mdi-magnify"
          density="comfortable"
          hide-details
          :clearable="true"
          :disabled="loading"
          :persistent-clear="true"
          @click:clear="clearSearch"
          @keydown.enter="searchChanged"
          @click:append-inner="searchChanged"
        />
      </v-col>
    </v-row>

    <v-row v-if="willLeaveUserWithoutOrganizations" dense>
      <v-col>
        <v-alert type="warning" variant="outlined" border="start">
          {{ t('lcp.no_organization_selected_warning') }}
        </v-alert>
      </v-col>
    </v-row>

    <v-row dense>
      <v-col>
        <OrganizationsTable v-model="associationsState" :loading="loading" :search="searchString" :user="props.user" />
      </v-col>
    </v-row>
  </Modal>
</template>
